<template>
  <div>
    <QuoteDetailSeasonalHeader
      v-if="displaySeasonalHeader"
      :scope-name="headerScopeName"
      :seasonality-fluctuation-months="quoteLineItemForPack.seasonality_fluctuation_months"
      :commitment="commitment"
      :payment_frequency="payment_frequency"
      :additional-user-price="extraUsersPricing"
      :locale="quote.locale"
    />
    <div
      v-else
      :class="quoteDetailClass"
    >
      <div class="quote-detail__header__scope-name">
        {{ headerScopeName }}
      </div>
      <div
        v-if="withCommitmentDetails"
        class="quote-detail__header__commitment-details"
      >
        <div class="quote-detail__header__commitment-details__content">
          <span class="quote-detail__header__commitment-details__content__detail">
            {{ $t('quote_details_block.included_users') }}
          </span>
          <span class="quote-detail__header__commitment-details__content__detail">
            <span class=" quote-detail__header__commitment-details__content__detail--highlighted">
              {{ $t('quote_details_block.up_to', {maxUsers: maxUsersForPack}) }}
            </span>
            &nbsp;{{ extraUsersPricing }}
          </span>
        </div>
        <div class="quote-detail__header__commitment-details__content">
          <div
            class="
              quote-detail__header__commitment-details__content__detail
              quote-detail__header__commitment-details__content__detail--flex-end
            "
          >
            <span>
              {{ $t('quote_details_block.commitment') }}
            </span>
            <span
              class="
              quote-detail__header__commitment-details__content__detail--highlighted"
            >
              &nbsp;{{ commitment }}
            </span>
          </div>
          <div
            class="
              quote-detail__header__commitment-details__content__detail
              quote-detail__header__commitment-details__content__detail--flex-end
            "
          >
            <span>
              {{ $t('quote_details_block.frequency') }}
            </span>
            <span class="quote-detail__header__commitment-details__content__detail--highlighted">
              &nbsp;{{ payment_frequency }}
            </span>
          </div>
        </div>
      </div>
    </div>
    <div :class="quoteDetailContentClass">
      <div
        v-for="(item, index) in quoteLineItems"
        :key="index"
      >
        <div class="quote-detail__content__item">
          <div class="quote-detail__content__item__name">
            {{ item.title }}
            <SkOroraTag
              v-if="item.discounts[0]?.discount_amount"
              variant="blue"
              class="header-tag"
            >
              {{ displayedDiscount(item.discounts[0].discount_amount) }}
            </SkOroraTag>
          </div>
          <div class="quote-detail__content__item__price">
            <span
              v-if="isDiscounted(item)"
              class="quote-detail__content__item__price--discounted"
            >
              {{ amountWithoutDiscount(item) }}
            </span>
            {{ totalAmountWithDiscount(item) }}
          </div>
        </div>
      </div>
      <div
        v-if="withTotal"
        class="quote-detail__content__item quote-detail__content__item--total"
      >
        <div class="quote-detail__content__item__name">
          {{ $t('quote_details.totals.total_amount_without_tax') }}
        </div>
        <div class="quote-detail__content__item__price">
          {{ cumulatedTotalAmountWithDiscount }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { SkOroraTag } from '@skelloapp/skello-ui';
import { QuoteLineItemSeasonalityType } from '@skelloapp/svc-billing-automation-sdk';
import { extractPrice } from '@/shared/utils/method_helper';
import QuoteDetailSeasonalHeader from '@/shared/components/QuoteDetailSeasonalHeader/index.vue';

export default {
  name: 'QuoteDetailBlock',
  components: {
    SkOroraTag,
    QuoteDetailSeasonalHeader,
  },
  props: {
    quote: {
      type: Object,
      required: true,
    },
    scopeName: {
      type: String,
      required: false,
      default: '',
    },
    shop: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    quoteLineItems: {
      type: Array,
      required: true,
    },
    isEnterpriseCustomer: {
      type: Boolean,
      required: false,
      default: false,
    },
    withCommitmentDetails: {
      type: Boolean,
      required: false,
      default: false,
    },
    withTotal: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  computed: {
    quoteDetailClass() {
      return this.withCommitmentDetails ?
        'quote-detail__header' :
        'quote-detail__header__without-commitment';
    },
    quoteDetailContentClass() {
      return this.withTotal ?
        'quote-detail__content' :
        'quote-detail__content__without-total';
    },
    headerScopeName() {
      if (this.scopeName) return this.scopeName;

      return this.isEnterpriseCustomer ? this.quote.organisation.name : this.shop.name;
    },
    quoteLineItemForPack() {
      return this.quoteLineItems.filter(item => item.type.toLowerCase() === 'pack')[0];
    },
    maxUsersForPack() {
      return this.quoteLineItemForPack.max_users;
    },
    extraUsersPricing() {
      return this.$t(
        'quote_details_block.extra_users_pricing',
        {
          additionalUserPrice: extractPrice(
            this.quote,
            this.quoteLineItems.reduce((acc, item) => acc + item.additional_user_price, 0),
          ),
        },
      );
    },
    cumulatedTotalAmountWithDiscount() {
      return extractPrice(
        this.quote,
        this.quoteLineItems.reduce((acc, item) => acc + item.total_amount_with_discount, 0),
      );
    },
    commitment() {
      return this.$t(`quote_details.commitment.${this.shop.contract_terms.commitment.toLowerCase().split(' ').join('_')}`);
    },
    payment_frequency() {
      return this.$t(`quote_details.billing_cycle.${this.shop.contract_terms.payment_frequency.toLowerCase().split(' ').join('_')}`);
    },
    isSeasonalOffer() {
      return this.quoteLineItemForPack?.seasonality === QuoteLineItemSeasonalityType.FLUCTUATIONS;
    },
    displaySeasonalHeader() {
      return this.isSeasonalOffer &&
        this.headerScopeName &&
        this.quoteLineItemForPack?.seasonality_fluctuation_months &&
        this.commitment &&
        this.payment_frequency &&
        this.extraUsersPricing &&
        this.quote?.locale;
    },
  },
  methods: {
    isDiscounted(item) {
      return item.discounts[0]?.discount_amount > 0;
    },
    amountWithoutDiscount(item) {
      return extractPrice(this.quote, item.total_amount);
    },
    totalAmountWithDiscount(item) {
      return extractPrice(this.quote, item.total_amount_with_discount);
    },
    displayedDiscount(discount) {
      return `-${discount}%`;
    },
  },
};
</script>

<style lang="scss" scoped>
.header-tag {
  margin-left: 4px;
}
.quote-detail__header {
  background-color: $sk-grey-5;
  padding: 12px 24px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.quote-detail__header__without-commitment {
  background-color: $sk-grey-5;
  padding: 8px 16px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.quote-detail__header__scope-name {
  font-size: $fs-heading-s;
  font-weight: $fw-semi-bold;
  line-height: 1;
}

.quote-detail__header__commitment-details {
  margin-top: 8px;
  display: flex;
  width: 100%;
  justify-content: space-between;
}

.quote-detail__header__commitment-details__content {
  display: flex;
  flex-direction: column;
}

.quote-detail__content__item__name {
  display: flex;
  align-items: center;
}

.quote-detail__content__item__price--discounted {
  text-decoration: line-through;
  margin-right: 4px;
  font-size: $fs-text-s;
  font-weight: $fw-regular;
  color: $sk-grey-50;
}

.quote-detail__header__commitment-details__content__detail {
  display: flex;
  color: $sk-grey-50;

  &--highlighted {
    color: $sk-black;
  }

  &--flex-end {
    justify-content: flex-end;
  }
}

.quote-detail__content {
  background-color: $sk-white;
  padding: 12px 24px 24px;
}

.quote-detail__content__without-total {
  background-color: $sk-white;
  padding: 12px 24px 8px 24px;
}

.quote-detail__content__item {
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;

  &--total {
    font-weight: $fw-semi-bold;
    font-size: $fs-text-l;
    margin-top: 12px;
    margin-bottom: 0;
  }
}
</style>
